import React, { useState, useContext, useEffect } from 'react';
import { AccountContext } from '../../../components/AccountContext';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import gql from 'graphql-tag';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { Loading } from '../../../components/Loading';
import { growlErrors } from '../../../utils/NotificationUtil';
import { useQuery } from '@apollo/react-hooks';
import { Button } from 'primereact/button';
import { saveInvoicesToSettle } from '../../../services/apps/InvoiceCommissionService';
import { AccountType } from '../../../shared/Constants';
import Invoice from '../../../components/Invoice';

const INVOICES_TO_SETTLE_QUERY = gql`
  query invoicesToSettleQuery($accountId: ID!) {
    account(id: $accountId) {
      type
      invoicesToSettle {
        idOfPayingAccount
        nameOfPayingAccount
        typeOfPayingAccount
        invoiceId
        invoiceNumber
        initialInvoiceValue
        commissionValue
        gpCommissionValue
        gpAccountName
      }
    }
  }
`;

const i18nMessages = defineMessages({
  invoiceCommission: {
    id: 'menu.invoice-commission',
    defaultMessage: 'Invoice commission',
  },
  client: { id: 'attrs.client', defaultMessage: 'Client' },
  id: { id: 'attrs.id', defaultMessage: 'ID' },
  invoice: { id: 'account.invoice-commissions.invoice', defaultMessage: 'Invoice' },
  yourCommission: { id: 'account.settled-commissions.your-commission', defaultMessage: 'Your commission' },
  gpCommission: { id: 'account.settled-commissions.gp-commission', defaultMessage: 'GP commission' },
  gpName: { id: 'account.settled-commissions.gp-name', defaultMessage: 'GP name' },
  noInvoices: {
    id: 'account.invoice-commissions.no-invoices',
    defaultMessage: 'There are currently no invoices to get commission from',
  },
});

const DEFAULT_ACCOUNT_STATE = {
  type: null,
  invoicesToSettle: [],
};

const InvoiceCommission = () => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();
  const history = useHistory();

  const [account, setAccount] = useState(DEFAULT_ACCOUNT_STATE);

  const { loading, error, data } = useQuery(INVOICES_TO_SETTLE_QUERY, {
    variables: {
      accountId: currentAccount,
    },
    fetchPolicy: 'no-cache',
  });

  const { account: accountFromQuery } = data;

  useEffect(() => {
    if (accountFromQuery) {
      setAccount(accountFromQuery);
    }
    // eslint-disable-next-line
  }, [accountFromQuery]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const { type, invoicesToSettle } = account;

  const handleCreatingInvoiceForUnsettledCommission = () => {
    saveInvoicesToSettle(invoicesToSettle);
    history.push(`/accounts/${currentAccount}/create-invoice-commission`);
  };

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel header={formatMessage(i18nMessages.invoiceCommission)}>
            {invoicesToSettle.length === 0 && (
              <div className="align-center font-weight-600">{formatMessage(i18nMessages.noInvoices)}</div>
            )}
            {invoicesToSettle.length > 0 && (
              <>
                <div className="font-weight-600 flex-space-between">
                  <div>{formatMessage(i18nMessages.client)}</div>
                  <div>{formatMessage(i18nMessages.id)}</div>
                  <div>{formatMessage(i18nMessages.invoice)}</div>
                  <div>{formatMessage(i18nMessages.yourCommission)}</div>
                  {type === AccountType.GSC && (
                    <>
                      <div>{formatMessage(i18nMessages.gpCommission)}</div>
                      <div>{formatMessage(i18nMessages.gpName)}</div>
                    </>
                  )}
                </div>
                <>
                  {invoicesToSettle.map((invoice) => {
                    return (
                      <div key={invoice.invoiceId} className="flex-space-between">
                        <div>{invoice.nameOfPayingAccount}</div>
                        <div>{invoice.idOfPayingAccount}</div>
                        <Invoice
                          managingAccountId={currentAccount}
                          clientAccountId={invoice.idOfPayingAccount}
                          invoiceId={invoice.invoiceId}
                          invoiceNumber={invoice.invoiceNumber}
                        />
                        <div>${invoice.commissionValue.toFixed(2)}</div>
                        {type === AccountType.GSC && (
                          <>
                            <div>{invoice.gpCommissionValue ? `$${invoice.gpCommissionValue.toFixed(2)}` : '-'}</div>
                            <div>{invoice.gpAccountName ? invoice.gpAccountName : '-'}</div>
                          </>
                        )}
                      </div>
                    );
                  })}
                  <br />
                  <div className="align-center">
                    <Button
                      type="button"
                      label={formatMessage(i18nMessages.invoiceCommission)}
                      onClick={handleCreatingInvoiceForUnsettledCommission}
                    />
                  </div>
                </>
              </>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCommission;
